import { ECONOMY } from './economy';
import { CLEARED_T_PER_DAY, OPENING_SHORE_T } from './shoreDebris';
import { dollars, earn, fundsLevel, isCleanupHome, isSurveyHome, monthsInHand } from './operations';
import type { GameEvent, World } from './types';
import { civilFromEpochDay, dateLabel, epochDayFromCivil, epochDayOf } from './calendar';

/**
 * The first campaign, "Maui Nui: The Reef at a Crossroads" (M239, the overhaul's brief, section 8): four chapters over
 * the first year, each objective read from the world as it stands (the operations, the management, the monitoring, the
 * heat), never scripted, so any way to it counts and a setback is the world's own; then the first annual review on the
 * year's last day, and the sanctuary run on from it. Three objectives are the player's looking (the Reef explored, the
 * coral's monitoring read, the threats read), marked by `campaign_mark`, the one way the interface tells the core what
 * the player saw. Nothing here moves an animal: an objective is read, and the review's award is the conservancy's
 * money.
 */

export type ChapterId = 'firstSurvey' | 'difficultChoice' | 'firstTest' | 'futureWorthProtecting';
export const CHAPTERS: readonly ChapterId[] = ['firstSurvey', 'difficultChoice', 'firstTest', 'futureWorthProtecting'];
/**
 * Each chapter named for the decision the player makes in it (M333, `docs/ONE_REEF_ROADMAP.md`'s O8, the playtest's "The
 * chapter titles promise more agency than the actual interactions deliver": Chapter 2, "A Difficult Choice", had opened
 * after its choice was made): the boat's first use; a rule or a patrol for the grazing fish; the heat's toll counted or
 * the plan kept; what the year's last months buy, to its review. The ids kept, the saves' (M239).
 */
export const CHAPTER_NAMES: Readonly<Record<ChapterId, string>> = {
  firstSurvey: 'What the Boat Does First', difficultChoice: 'Rule or Patrol', firstTest: 'Count the Heat or Carry On', futureWorthProtecting: 'What the Last Months Buy',
};

/** What the player has looked at, which the core cannot see. */
export const MARKS = ['exploredReef', 'readCoral', 'readThreats', 'skippedGuide'] as const;
export type Mark = (typeof MARKS)[number];

/** A review's reading of one of its six questions. */
export type Grade = 'good' | 'fair' | 'poor';

/** The review's six readings, in the order it gives them (the brief's list). */
export const REVIEW_LINES = ['monitoring', 'operations', 'protection', 'trajectory', 'community', 'achievements'] as const;

export interface ReviewLine {
  id: (typeof REVIEW_LINES)[number];
  label: string;
  grade: Grade;
  text: string;
}

export interface AnnualReview {
  /** The epoch day it was held. */
  day: number;
  lines: ReviewLine[];
  /** The performance grant it awarded, US dollars (none below its bar). */
  award: number;
}

export interface Campaign {
  /** The chapter under way, its index in `CHAPTERS`; `CHAPTERS.length` once the review is held. */
  chapter: number;
  /** The epoch day each chapter began. */
  chapterDays: number[];
  /** Each objective done, by its id, the epoch day it was. */
  done: Record<string, number>;
  /** The player's looking, by mark, the epoch day it was first seen. */
  marks: Partial<Record<Mark, number>>;
  /** The test chapter 3 met: a marine heatwave the sanctuary went through, or the warm season's (none yet). */
  test: 'heatwave' | 'season' | null;
  /** The epoch day the next annual review is held: each year's last day, the first the opening's year's. */
  reviewDay: number;
  /** The annual reviews held, oldest first (M240: each year's, the first the campaign's last chapter's). */
  reviews: AnnualReview[];
}

/** One objective: its words, why it matters, the way to it the objective card offers, and whether it is done. */
export interface Objective {
  id: string;
  title: string;
  why: string;
  /** The objective card's button: a destination or a page (`shell.ts` reads it). */
  action: { label: string; act: string } | null;
  met(world: World): boolean;
}

/** The surveys finished on or after a day (a shore cleanup is a mission, not a survey: M307). */
const surveysSince = (world: World, day: number): number => world.operations.finished.filter((f) => isSurveyHome(f) && f.endDay >= day).length;
const mark = (world: World, m: Mark): boolean => world.campaign.marks[m] !== undefined;
const patrolled = (world: World): boolean => (world.sanctuaries[0]?.management?.patrols ?? []).some((p) => p > 0);
const ruleUnderWay = (world: World): boolean => world.operations.jobs.some((j) => j.kind === 'rule') || world.operations.finished.some((f) => f.kind === 'rule');
/**
 * A survey home after the response began, and not the first survey (M262: the first, home the day patrols were paid
 * for, or at sea when they were, had met "Survey the reef again" too; `baseline`, read before it in the same day, marks
 * the day it came home).
 */
const secondSurvey = (world: World): boolean => {
  const begun = world.campaign.done.beginManagement;
  const first = world.campaign.done.baseline;
  return begun !== undefined && world.operations.finished.some((f) => isSurveyHome(f) && f.endDay > begun && f.endDay !== first);
};
/** A rule's consultation over (its liaison freed) or the rule made. */
const consulted = (world: World): boolean => world.operations.finished.some((f) => f.kind === 'rule')
  || world.operations.jobs.some((j) => j.kind === 'rule' && epochDayOf(world) >= (j.phaseEnds[0] as number));

/**
 * Each chapter's objectives, in order (the brief's, each read from the world). Done in any order; a chapter closes when
 * all its own are, the third on its test's, the fourth on the review.
 */
export const OBJECTIVES: Readonly<Record<ChapterId, readonly Objective[]>> = {
  // M262 (the user's brief, "one objective, one obvious action"): each in plain words, its button the way to it, the
  // first ten minutes' loop in order: look at the reef, read the coral's report, send the survey, wait for it, read its
  // findings, choose a response, see it begin, survey again.
  firstSurvey: [
    { id: 'exploreReef', title: 'Explore Maui Nui Reef', why: 'Look around the reef you now protect: its coral, its fish and the water over them.', action: { label: 'Dive in', act: 'explore-reef' }, met: (w) => mark(w, 'exploredReef') },
    // M314 (the opening loop's G4, the fourth playtest's: "Put cleanup ahead of surveys, give the player a visible first
    // accomplishment"): the boat's first trip a shore's nets, Lānaʻi's the most at the opening (M307); met once it is
    // sent, its tonnes home on their own five days on; titled as the act it is, the crew sent (M319: "Done: Clear nets from
    // Lānaʻi's shore" was told the day the boat sailed, five days before the nets were off).
    { id: 'clearShore', title: 'Send a crew to clear Lānaʻi\'s nets', why: `Lānaʻi's shore held the most derelict fishing nets in Maui Nui at the opening, about ${Math.round(OPENING_SHORE_T.lanai)} tonnes. A ${ECONOMY.surveyDays[0]}-day trip of your boat and a dive team clears about ${(CLEARED_T_PER_DAY * (ECONOMY.surveyDays[0] as number)).toFixed(1)} t of it, the first nets off within the week.`, action: { label: 'Clear Lānaʻi\'s shore', act: 'reef:cleanup' }, met: (w) => w.operations.jobs.some((j) => j.survey === 'shoreCleanup') || w.operations.plan.some((p) => p.kind === 'shoreCleanup') || w.operations.finished.some(isCleanupHome) },
    { id: 'reviewCoral', title: 'Read the coral report', why: 'Before you protect a reef, learn whether its coral is still falling. NOAA\'s divers last counted it in 2024: see what they found.', action: { label: 'Open the coral report', act: 'coral' }, met: (w) => mark(w, 'readCoral') },
    // M289 (the playtest's second and third points): a choice of what the one boat learns first, the coral or its grazing
    // fish; on the boat's card since M329 (O4), beside its cleanup, the coral's count lit.
    { id: 'dispatchSurvey', title: 'Choose your first survey', why: 'Your one boat makes one survey at a time. Count the coral the report flags, to learn whether its fall goes on, or the fish that graze it, the baseline protection is judged against. The other waits for the boat.', action: { label: 'See the boat\'s trips', act: 'reef:survey:reefBenthic' }, met: (w) => w.operations.jobs.some((j) => j.kind === 'survey' && j.survey !== 'shoreCleanup') || w.operations.plan.some((p) => p.kind !== 'shoreCleanup') || surveysSince(w, 0) > 0 },
    { id: 'baseline', title: 'Wait for the survey to come home', why: 'The divers count the reef for days on end. Let the days run, or skip ahead to their return.', action: { label: 'Advance to its return', act: 'advance' }, met: (w) => surveysSince(w, 0) > 0 },
    // Read only on the findings' page once a survey is home (`readThreats`, its id kept for the saves that hold it).
    { id: 'identifyConcern', title: 'Read the survey\'s findings', why: 'See what the divers counted, how it compares with the last count, and what it means for the reef.', action: { label: 'Open the findings', act: 'findings' }, met: (w) => mark(w, 'readThreats') },
  ],
  difficultChoice: [
    { id: 'chooseIntervention', title: 'Choose how to protect the reef', why: 'Pay for patrols that begin at once, or propose a rule that protects the reef\'s grazing fish. Each acts on the fishing; neither on the heat.', action: { label: 'See the options', act: 'reef:protect' }, met: (w) => ruleUnderWay(w) || patrolled(w) },
    { id: 'beginManagement', title: 'See your response take effect', why: 'Patrols begin the day they are paid for. A rule is first talked through with the island\'s communities, about four months, then made by the State.', action: { label: 'Advance to the next step', act: 'advance' }, met: (w) => patrolled(w) || consulted(w) },
    { id: 'assessResults', title: 'Survey the reef again', why: 'Once management has begun, count the reef again to see where it stands. A year is too short to see recovery, but not to know where things are.', action: { label: 'Plan the survey', act: 'survey' }, met: (w) => secondSurvey(w) },
  ],
  firstTest: [
    { id: 'meetTest', title: 'See what the warm season brings', why: 'Late summer is when Maui Nui\'s water is warmest and its coral most at risk of bleaching: a marine heatwave, or the season passing without one.', action: { label: 'Advance to the warm season', act: 'advance' }, met: (w) => w.campaign.test !== null },
    { id: 'surveyAfter', title: 'Survey the coral after the heat', why: 'Only a count shows what the warm season did to the coral.', action: { label: 'Plan a coral survey', act: 'survey-coral' }, met: (w) => w.campaign.test !== null && w.operations.finished.some((f) => f.kind === 'survey' && (f.title.startsWith('Reef benthic') || f.title.startsWith('Bleaching assessment')) && f.endDay >= (w.campaign.done.meetTest ?? Infinity)) },
    { id: 'staySolvent', title: 'Stay out of debt through the season', why: 'Debt ends the patrols the conservancy pays for. Close every month from July to 31 October with money in hand.', action: { label: 'Check the accounts', act: 'operations' }, met: (w) => solventThroughSeason(w) },
  ],
  futureWorthProtecting: [
    { id: 'annualReview', title: 'Complete the first annual review', why: 'On the year\'s last day the conservancy\'s year is reviewed: what it learned, what it protected, what it can sustain.', action: { label: 'Advance to the review', act: 'advance' }, met: (w) => w.campaign.reviews.length > 0 },
  ],
};

/**
 * Objectives the campaign no longer asks (M262: two of the second chapter's were met by the very act of choosing),
 * kept so a save that met them still loads; never shown, never counted.
 */
export const RETIRED_OBJECTIVES: readonly string[] = ['commitResources', 'engageCommunities'];

/** The first year's warm season, 1 July to 31 October (`SEASON`), as epoch days (the objective card's too, review M267's). */
export function seasonOf(c: Campaign): { from: number; end: number } {
  const year = civilFromEpochDay(c.chapterDays[0] as number).year;
  return { from: epochDayFromCivil(year, SEASON.fromMonth, 1), end: epochDayFromCivil(year, SEASON.endMonth, SEASON.endDay) };
}

/** Whether every month from July to the season's end closed out of debt, read once the season is over. */
function solventThroughSeason(w: World): boolean {
  const { from, end } = seasonOf(w.campaign);
  const year = civilFromEpochDay(from).year;
  if (epochDayOf(w) < end) return false;
  const months = w.operations.months.filter((m) => m.year === year && m.month >= SEASON.fromMonth && m.month <= SEASON.endMonth);
  return months.length === SEASON.endMonth - SEASON.fromMonth + 1 && months.every((m) => m.funds >= 0);
}

/**
 * The warm season's test, read from the log (the same in play and in a v40 save's replay, which holds the log and not
 * the heatwave's state day by day; review M239's): a marine heatwave around the sanctuary under way on 1 July or begun
 * by the season's end, else the season itself from its last day on; none before 1 July. Today's own events count.
 */
function seasonTest(world: World, events: readonly GameEvent[]): Campaign['test'] {
  const s = world.sanctuaries[0];
  const c = world.campaign;
  const today = epochDayOf(world);
  const { from, end } = seasonOf(c);
  if (!s || today < from) return null;
  const begun = `A marine heatwave around ${s.site.placeName}`;
  const over = `The marine heatwave around ${s.site.placeName} is over`;
  let active = false;
  for (const e of [...world.log.filter((x) => x.day <= world.day), ...events]) {
    const day = world.startEpochDay + e.day;
    if (day > Math.min(today, end)) break;
    if (e.title === begun) {
      active = true;
      if (day >= from) return 'heatwave';
    } else if (e.title === over) active = false;
    else continue;
  }
  if (active) return 'heatwave';
  return today >= end ? 'season' : null;
}

/** A new conservancy's campaign: chapter one from its first day, its review on its year's last. */
export function openingCampaign(world: Pick<World, 'startEpochDay' | 'day'>): Campaign {
  const today = epochDayOf(world);
  return { chapter: 0, chapterDays: [today], done: {}, marks: {}, test: null, reviewDay: epochDayFromCivil(civilFromEpochDay(today).year, 12, 31), reviews: [] };
}

/**
 * A mark of what the player saw (a `campaign_mark` command), the first day kept; false where it was seen already. The
 * findings' mark alone keeps its latest day (M319: each survey's findings are read in turn, and an island's "Next: Read
 * its findings" stood after they were); no objective reads the day, only whether the mark is there.
 */
export function markSeen(world: World, m: Mark): boolean {
  if (!MARKS.includes(m)) return false;
  const was = world.campaign.marks[m];
  const today = epochDayOf(world);
  if (was !== undefined && (m !== 'readThreats' || was >= today)) return false;
  world.campaign.marks[m] = today;
  return true;
}

/**
 * The objective to show: the first of the chapter's not done; where the chapter's own are all done but the day's tick
 * has not yet closed it, the next chapter's first not done (M262: the card had shown the year's end between a chapter's
 * last objective met by a press and the next day); none once all is done.
 */
export function currentObjective(world: World): { chapter: ChapterId; index: number; objective: Objective } | null {
  const c = world.campaign;
  for (let k = c.chapter; k < CHAPTERS.length; k++) {
    const chapter = CHAPTERS[k] as ChapterId;
    const list = OBJECTIVES[chapter];
    // The guide put away is not shown (M303); its steps passed are not waited on.
    const left = chapter === 'firstSurvey' && guideSkipped(c) ? [] : stepsLeft(c, chapter);
    const index = left.length > 0 ? list.indexOf(left[0] as Objective) : -1;
    if (index >= 0) return { chapter, index, objective: list[index] as Objective };
    // A chapter the review has closed whatever was undone is not waited on (`stepCampaign`).
    if (c.reviews.length > 0) return null;
  }
  return null;
}

/**
 * The warm season: from 1 July, a marine heatwave the sanctuary goes through is the third chapter's test; without one,
 * the season's last day, 31 October (JUDGED: Maui Nui's warmest water runs August to October, the coral's own warmest
 * month taken from the surface's, `reef.ts`). It comes whatever chapter the player is in: the season does not wait.
 */
export const SEASON = { fromMonth: 7, endMonth: 10, endDay: 31 } as const;

/**
 * The day's campaign, after the operations: each objective met marked done (any chapter's, so work done early counts);
 * a chapter closed when its own are; the third chapter's test the first marine heatwave the sanctuary goes through, or
 * the warm season's end; the review on the year's last day.
 */
export function stepCampaign(world: World, events: GameEvent[]): void {
  const c = world.campaign;
  const s = world.sanctuaries[0];
  // A campaign of the reef: none at a site without one (review M239's: its objectives could not be met there).
  if (!s?.coral || !s.fish) return;
  const today = epochDayOf(world);
  // The third chapter's test: the warm season's heatwave, or the season passing (`seasonTest`).
  if (c.test === null) c.test = seasonTest(world, events);
  // The day's objectives read before the review (review M239's: a survey home on 31 December counted in its monitoring
  // and not its achievements), and again after it, its own objective met.
  readObjectives(world);
  // Each year's review on its last day (M240: the annual report), the next set for the next year's.
  if (today >= c.reviewDay) {
    holdReview(world, events);
    c.reviewDay = epochDayFromCivil(civilFromEpochDay(c.reviewDay).year + 1, 12, 31);
    readObjectives(world);
  }
  // Chapters close in order; the review closes the last whatever is undone (the brief: genuine setbacks), its
  // chapters' openings then not announced (review M239's: three false openings logged on 31 December).
  while (c.chapter < CHAPTERS.length) {
    const ch = CHAPTERS[c.chapter] as ChapterId;
    const met = stepsLeft(c, ch).length === 0;
    // The guide put away closes its chapter, its opening not announced (M303's review: "The survey is back" with none sent).
    const skipped = ch === 'firstSurvey' && guideSkipped(c);
    if (!met && !skipped && c.reviews.length === 0) break;
    c.chapter++;
    if (c.chapter < CHAPTERS.length) {
      c.chapterDays.push(today);
      if (met && c.reviews.length === 0) events.push({ day: world.day, severity: 'good', title: `Chapter ${c.chapter + 1}: ${CHAPTER_NAMES[CHAPTERS[c.chapter] as ChapterId]}`, detail: chapterIntro(CHAPTERS[c.chapter] as ChapterId, c) });
    }
  }
}

/** The guide's steps passed once a later one is done (M303): the two that are looked at, not done in the world. */
// The cleanup's step too (M314): a player who goes to the survey first, and a save from before, never meet it behind them.
const PASSED_BY_LATER: ReadonlySet<string> = new Set(['exploreReef', 'clearShore', 'reviewCoral']);

/**
 * Each objective met marked done, the day it is (any chapter's, so work done early counts): run each day, and on a
 * command, so the objective card moves on as the player acts (review M239's: it waited for the next day).
 */
export function readObjectives(world: World): void {
  const c = world.campaign;
  const s = world.sanctuaries[0];
  if (!s?.coral || !s.fish || c.chapter >= CHAPTERS.length) return;
  const today = epochDayOf(world);
  CHAPTERS.forEach((ch) => {
    for (const o of OBJECTIVES[ch]) if (c.done[o.id] === undefined && o.met(world)) c.done[o.id] = today;
  });
}

/**
 * Whether a step of the first chapter's guide is passed (M303, the field loop's F2, the third playtest's: "after hiring,
 * completing a survey, and proposing protection, my tutorial still said Explore Maui Nui Reef, 1 of 5"): one that is
 * looked at, with a later step of the guide done. Passed is not done: it moves the card and lets the chapter close, but
 * marks nothing met, so the year's review counts only what was done (its review: passed and skipped steps had counted as
 * achievements). The guide's alone: a later chapter's steps are achievements of their own; and only what is looked at,
 * never a survey sent or come home, events in the world met by the world alone.
 */
export function passed(c: Campaign, id: string): boolean {
  const guide = OBJECTIVES.firstSurvey;
  const k = guide.findIndex((o) => o.id === id);
  return PASSED_BY_LATER.has(id) && k >= 0 && guide.slice(k + 1).some((o) => c.done[o.id] !== undefined);
}

/** A chapter's steps still to do: undone, and in the guide not passed. */
const stepsLeft = (c: Campaign, ch: ChapterId): readonly Objective[] => OBJECTIVES[ch].filter((o) => c.done[o.id] === undefined && !(ch === 'firstSurvey' && passed(c, o.id)));

/** Whether the player has put the guide away (M303, F2: "let experienced players dismiss it"). */
export const guideSkipped = (c: Campaign): boolean => c.marks.skippedGuide !== undefined;

/**
 * The first chapter's guide put away: a mark of its own, nothing marked met (its review: the steps marked done had counted
 * as the year's achievements, closed the chapter with "The survey is back" and let the first survey count as the second).
 * False where the guide is behind the player already.
 */
export function skipGuide(world: World): boolean {
  if (world.campaign.chapter > 0 || stepsLeft(world.campaign, 'firstSurvey').length === 0) return false;
  return markSeen(world, 'skippedGuide');
}

/** A chapter's opening words, short. */
function chapterIntro(ch: ChapterId, c: Campaign): string {
  switch (ch) {
    case 'difficultChoice': return 'The survey is back. Now choose how the conservancy will protect the reef, and what it will pay for it.';
    case 'firstTest': return c.test === null ? 'Management has begun. The year\'s warm season is coming; watch what it does, and keep the conservancy going.' : 'Management has begun. The warm season has come; survey what it did to the coral, and keep the conservancy going.';
    case 'futureWorthProtecting': return 'The year\'s last months: choose what the boat and the money do with them. On its last day the conservancy\'s first year is reviewed.';
    default: return '';
  }
}

/**
 * The review's performance grant: for each question read good, $25,000 (JUDGED: four good readings give $100,000, about
 * a seventh of the year's grant, recognition rather than rescue).
 */
export const AWARD_PER_GOOD = 25_000;

/** An annual review: six questions read from the year, its grant paid, logged. */
function holdReview(world: World, events: GameEvent[]): void {
  const lines = reviewLines(world);
  const award = lines.filter((l) => l.grade === 'good').length * AWARD_PER_GOOD;
  const ops = world.operations;
  // Paid as a grant, in the month's accounts.
  if (award > 0) earn(ops, 'grant', award);
  const first = world.campaign.reviews.length === 0;
  world.campaign.reviews.push({ day: epochDayOf(world), lines, award });
  events.push({ day: world.day, severity: award > 0 ? 'good' : 'info', title: first ? 'The first annual review' : `The ${civilFromEpochDay(epochDayOf(world)).year} annual review`, detail: `${lines.filter((l) => l.grade === 'good').length} of ${lines.length} read good; ${award > 0 ? `a performance grant of ${dollars(award)}` : 'no performance grant'}. Open Manage to read it.` });
}

/** The change between two counts in standard errors of the log of their ratio (each count's error log-normal, `logVariance`). */
export function declineZ(before: { value: number; cv: number | null }, after: { value: number; cv: number | null }): number {
  const v = (cv: number | null): number => (cv === null ? 0 : Math.log(1 + cv * cv));
  const sigma = Math.sqrt(v(before.cv) + v(after.cv));
  const ratio = Math.log(after.value / before.value);
  return sigma > 0 ? ratio / sigma : ratio > 0 ? Infinity : ratio < 0 ? -Infinity : 0;
}

/**
 * The review's six readings (the brief's "Evaluate" list), each from the year's record. Their bars are the game's
 * (JUDGED, each set against a year the campaign's chapters ask for): good from three surveys (the first, one after
 * management began, one after the season) and fair from one; good from six months of running costs in hand (a season's
 * surveys and the next grant within reach) and fair out of debt; good with an island protected and one patrolled,
 * fair with either; good from two consultations finished, fair from one; good from all but two of the objectives met,
 * fair from half.
 */
export function reviewLines(world: World): ReviewLine[] {
  const c = world.campaign;
  const ops = world.operations;
  // The year since the last review, or since the opening.
  const lastReview = c.reviews.at(-1);
  const yearStart = lastReview ? lastReview.day + 1 : c.chapterDays[0] ?? world.startEpochDay;
  const surveys = surveysSince(world, yearStart);
  // Read as of the day (a replayed world's monitoring holds the counts after it).
  const today = epochDayOf(world);
  const counted = Object.values(world.monitoring).filter((r) => r.some((x) => x.day >= yearStart && x.day <= today)).length;
  const m = world.sanctuaries[0]?.management;
  const protectedIslands = m ? m.herbivoreProtection.filter(Boolean).length : 0;
  const patrolledIslands = m ? m.patrols.filter((p) => p > 0).length : 0;
  // The year's own (M240's review: every rule and consultation ever finished had counted each year): a rule made since
  // the year began, a consultation over since then (a made rule's ended its State step's days before it was made).
  const rules = ops.finished.filter((f) => f.kind === 'rule' && f.endDay >= yearStart).length;
  const consultations = ops.finished.filter((f) => f.kind === 'rule' && f.endDay - ECONOMY.rule.stateDays >= yearStart && f.endDay - ECONOMY.rule.stateDays <= today).length
    + ops.jobs.filter((j) => j.kind === 'rule' && (j.phaseEnds[0] as number) >= yearStart && today >= (j.phaseEnds[0] as number)).length;
  const months = monthsInHand(world);
  const coral = (world.monitoring.coral ?? []).filter((r) => r.day <= today);
  const [before, after] = [coral.filter((r) => r.day < yearStart).at(-1), coral.filter((r) => r.day >= yearStart).at(-1)];
  // The guide's cleanup step left out of the year's count (M314's review: a step the guide lets a player pass had raised
  // the bar for "good", $25,000 of the award, for a player who went to the survey first).
  const done = Object.keys(c.done).filter((id) => !RETIRED_OBJECTIVES.includes(id) && id !== 'clearShore').length;
  const all = CHAPTERS.reduce((n, ch) => n + OBJECTIVES[ch].filter((o) => o.id !== 'clearShore').length, 0) - 1;
  // After the first year the campaign's objectives are all behind it (M240's review: each later review had read them
  // good again): its achievements the year's work finished, surveys home and rules made, good at four, fair at two
  // (JUDGED: about a survey a season).
  const work = ops.finished.filter((f) => f.endDay >= yearStart && f.endDay <= today).length;
  const grade = (good: boolean, fair: boolean): Grade => (good ? 'good' : fair ? 'fair' : 'poor');
  return [
    { id: 'monitoring', label: 'Monitoring progress', grade: grade(surveys >= 3, surveys >= 1), text: `${surveys} ${surveys === 1 ? 'survey' : 'surveys'} of the conservancy's own; ${counted} ${counted === 1 ? 'program' : 'programs'} counted this year.` },
    { id: 'operations', label: 'Operational sustainability', grade: fundsLevel(world), text: ops.funds < 0 ? 'The conservancy ends the year in debt.' : `${Number.isFinite(months) ? `About ${Math.floor(months)} ${Math.floor(months) === 1 ? 'month' : 'months'} of running costs` : 'Running costs covered'} in hand.` },
    { id: 'protection', label: 'Protection effectiveness', grade: grade(protectedIslands > 0 && patrolledIslands > 0, protectedIslands > 0 || patrolledIslands > 0), text: `${protectedIslands} ${protectedIslands === 1 ? 'island' : 'islands'} with a Herbivore Protection Area in force; ${patrolledIslands} with patrols the conservancy pays for.` },
    {
      // Read against the two counts' errors, as the overview's trend is (`trendOf`): poor only past two standard errors'
      // decline, fair past one, else good (the brief: no dramatic recovery asked of one year).
      id: 'trajectory', label: 'Ecological trajectory', grade: before && after ? (() => { const z = declineZ(before, after); return grade(z > -1, z > -2); })() : 'fair',
      text: before && after ? `Live coral ${after.value.toFixed(1)}% on the year's last count, against ${before.value.toFixed(1)}% before it${Math.abs(declineZ(before, after)) < 2 ? ', within the counts\' error' : ''}. A reef recovers over years, not one.` : 'No count of the coral this year to set against the last: the trajectory is not known.',
    },
    { id: 'community', label: 'Community support', grade: grade(consultations >= 2, consultations >= 1), text: `${consultations} ${consultations === 1 ? 'consultation' : 'consultations'} with the islands' communities finished; ${rules} ${rules === 1 ? 'rule' : 'rules'} made with them.` },
    lastReview
      ? { id: 'achievements', label: 'Conservation achievements', grade: grade(work >= 4, work >= 2), text: `${work} ${work === 1 ? 'piece' : 'pieces'} of the conservancy's work finished this year, surveys and cleanups home and rules made.` }
      : { id: 'achievements', label: 'Conservation achievements', grade: grade(done >= all - 2, done >= all / 2), text: `${done} of the year's ${all} objectives met.` },
  ];
}

/** When the review is held, in words. */
export const reviewWhen = (c: Campaign): string => dateLabel(c.reviewDay);

/** The next of the conservancy's goals once the campaign is done: the next survey cycle, kept short. */
export const ONGOING = 'The sanctuary is yours to run: survey, manage, and let the next annual review read what the reef does.';
